import { getWorkspaceId } from '../../lib/auth-storage';
import { useCallback, useEffect, useState } from 'react';
import {
  aiCatalogApi,
  BULK_ACTIONS,
  type ActivityEntry,
  type AnalysisResult,
  type DashboardSummary,
  type ListingGridRow,
  type OptimizerNotification,
  type OptimizationWorkflow,
  type Recommendation,
  type SideBySideComparison,
} from '../../api/aicatalog';

const WORKSPACE = (getWorkspaceId() ?? '');

const SAMPLE_LISTING = {
  asin: 'B000DEMO01',
  sku: 'DEMO-SKU-001',
  title: 'Acme Premium Widget Tool Set',
  bullets: ['Durable steel construction', 'Ergonomic grip handle', 'Multi-purpose design', 'Includes carry case'],
  description: 'Professional-grade widget tool for home and workshop use. Built to last.',
  brand: 'Acme',
  category: 'Tools > Hand Tools',
  backend_keywords: 'widget tool steel professional',
  image_count: 5,
};

export function ListingOptimizerDashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [rows, setRows] = useState<ListingGridRow[]>([]);
  const [total, setTotal] = useState(0);
  const [notifications, setNotifications] = useState<OptimizerNotification[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [comparisons, setComparisons] = useState<SideBySideComparison[]>([]);
  const [workflow, setWorkflow] = useState<OptimizationWorkflow | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, grid, act, notifs] = await Promise.all([
        aiCatalogApi.dashboard(WORKSPACE),
        aiCatalogApi.optimizerGrid(WORKSPACE),
        aiCatalogApi.listActivity(undefined, 20),
        aiCatalogApi.listNotifications(true),
      ]);
      setDashboard(dash);
      setRows(grid.rows);
      setTotal(grid.total);
      setActivity(act);
      setNotifications(notifs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load optimizer');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runAnalyze = async () => {
    setBusy(true);
    try {
      await aiCatalogApi.analyze({ workspace_id: WORKSPACE, listing: SAMPLE_LISTING });
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const loadAnalysis = async (id: string) => {
    setSelectedId(id);
    try {
      const [a, cmp, wf] = await Promise.all([
        aiCatalogApi.getAnalysis(id),
        aiCatalogApi.compare(id),
        aiCatalogApi.workflow(id),
      ]);
      setAnalysis(a);
      setComparisons(cmp);
      setWorkflow(wf);
    } catch {
      setAnalysis(null);
    }
  };

  const approveRec = async (rec: Recommendation) => {
    await aiCatalogApi.transitionRecommendation(rec.id, 'approved');
    if (selectedId) loadAnalysis(selectedId);
    refresh();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: string) => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      await aiCatalogApi.triggerBulk({ action, analysis_ids: [...selectedIds], workspace_id: WORKSPACE });
      setSelectedIds(new Set());
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const runSearch = async () => {
    if (!searchText.trim()) return;
    setBusy(true);
    try {
      await aiCatalogApi.globalSearch(searchText, WORKSPACE);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Listing Optimizer</h1>
          <p className="mt-1 text-sm text-slate-600">
            Explainable AI listing analysis, SEO intelligence, compliance validation, and publishing readiness
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runAnalyze}
            disabled={busy}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            Analyze Sample Listing
          </button>
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      )}

      {dashboard && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {[
            { label: 'Analyses', value: dashboard.total_analyses },
            { label: 'Recommendations', value: dashboard.total_recommendations },
            { label: 'Pending Review', value: dashboard.pending_review },
            { label: 'Approved', value: dashboard.approved_count },
            { label: 'Avg Health', value: `${Math.round(dashboard.avg_health_score * 100)}%` },
            { label: 'Notifications', value: dashboard.unread_notifications },
          ].map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase text-slate-400">{c.label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {workflow && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
          <h2 className="font-semibold text-emerald-900">Optimization Workflow</h2>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-emerald-800">
            {workflow.steps.map((s) => (
              <span key={s.name} className="rounded-full bg-white/80 px-3 py-1 capitalize">
                {s.name.replace('_', ' ')}: {s.state}
              </span>
            ))}
            {workflow.publish_ready && <span className="font-semibold text-emerald-700">Publish Ready</span>}
          </div>
        </section>
      )}

      {notifications.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-semibold">Notifications ({notifications.length})</h2>
          <ul className="mt-2 space-y-1 text-sm text-slate-600">
            {notifications.slice(0, 5).map((n) => (
              <li key={n.id}>
                <strong>{n.title}</strong> — {n.body}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search recommendations, keywords…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          className="min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <button type="button" disabled={busy} onClick={runSearch} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
          Search
        </button>
        {BULK_ACTIONS.map((action) => (
          <button
            key={action}
            type="button"
            disabled={busy || selectedIds.size === 0}
            onClick={() => runBulk(action)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium capitalize disabled:opacity-40"
          >
            Bulk {action}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2 space-y-4">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 flex justify-between">
              <h2 className="font-semibold">Listing Optimizer Grid</h2>
              <span className="text-sm text-slate-500">{total} listings</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2">Listing</th>
                    <th className="px-3 py-2">Health</th>
                    <th className="px-3 py-2">SEO</th>
                    <th className="px-3 py-2">Compliance</th>
                    <th className="px-3 py-2">Recs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr
                      key={row.analysis_id}
                      className={`cursor-pointer hover:bg-slate-50 ${selectedId === row.analysis_id ? 'bg-brand-50' : ''}`}
                      onClick={() => loadAnalysis(row.analysis_id)}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.analysis_id)} onChange={() => toggleSelect(row.analysis_id)} />
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium">{row.title ?? row.asin ?? '—'}</p>
                        <p className="text-xs text-slate-500 font-mono">{row.asin}</p>
                      </td>
                      <td className="px-3 py-2">{Math.round(row.health_score * 100)}%</td>
                      <td className="px-3 py-2">{Math.round(row.seo_score * 100)}%</td>
                      <td className="px-3 py-2">{Math.round(row.compliance_score * 100)}%</td>
                      <td className="px-3 py-2">
                        {row.rec_count}
                        {row.pending_review > 0 && <span className="ml-1 text-amber-600">({row.pending_review} review)</span>}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No analyses yet. Click &quot;Analyze Sample Listing&quot; to start.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {comparisons.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-semibold">Side-by-Side Comparison</h2>
              <div className="mt-3 space-y-3">
                {comparisons.map((c) => (
                  <div key={c.recommendation_id ?? c.field} className="rounded-lg border border-slate-100 p-3 text-sm">
                    <p className="font-medium capitalize">{c.field.replace('_', ' ')}</p>
                    <div className="mt-2 grid gap-2 md:grid-cols-2">
                      <div className="rounded bg-red-50 p-2">
                        <p className="text-xs text-red-600 font-semibold">Current</p>
                        <p className="text-slate-700">{c.current || '—'}</p>
                      </div>
                      <div className="rounded bg-emerald-50 p-2">
                        <p className="text-xs text-emerald-600 font-semibold">Suggested</p>
                        <p className="text-slate-700">{c.suggested}</p>
                      </div>
                    </div>
                    {c.explanation?.summary && <p className="mt-2 text-xs text-slate-500">{c.explanation.summary}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="space-y-4">
          {analysis ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
              <h2 className="font-semibold">Analysis Details</h2>
              <p className="text-sm font-medium">{analysis.listing.title}</p>
              <dl className="grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">Health</dt>
                <dd>{Math.round(analysis.health.overall_score * 100)}%</dd>
                <dt className="text-slate-500">SEO Coverage</dt>
                <dd>{Math.round(analysis.seo.keyword_coverage * 100)}%</dd>
                <dt className="text-slate-500">Compliance</dt>
                <dd>{Math.round(analysis.compliance.overall_compliance * 100)}%</dd>
                <dt className="text-slate-500">Prompt</dt>
                <dd className="text-xs">{analysis.prompt_version ?? '—'}</dd>
              </dl>
              {analysis.recommendations && analysis.recommendations.length > 0 && (
                <div>
                  <p className="text-sm font-medium">AI Recommendations</p>
                  <ul className="mt-1 space-y-2">
                    {analysis.recommendations.map((rec) => (
                      <li key={rec.id} className="rounded border border-slate-100 p-2 text-xs">
                        <p className="font-medium capitalize">{rec.type} · {rec.status}</p>
                        <p className="text-slate-600 mt-1">{rec.suggested.slice(0, 120)}{rec.suggested.length > 120 ? '…' : ''}</p>
                        {rec.status === 'draft' && (
                          <button type="button" onClick={() => approveRec(rec)} className="mt-1 text-brand-600 hover:underline">
                            Approve
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">
              Select a listing to view analysis, comparisons, and AI recommendations
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold">Optimization History</h2>
            <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto text-xs text-slate-600">
              {activity.map((a) => (
                <li key={a.id}>
                  <span className="text-slate-400">{new Date(a.created_at).toLocaleString()}</span>
                  <br />
                  {a.message}
                </li>
              ))}
              {activity.length === 0 && <li>No recent activity</li>}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
